import {
	Text,
	HStack,
	Button,
	Input,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { RequestStatus } from '@montel/montelpro-shared-components/enums';
import { ACCOUNT_API_ROUTE } from 'apiRoutes';
import { StandardModal } from 'components/common/Modal/StandardModal';
import { Email, MontelAccount } from 'domain/account/interfaces';
import useSubmit, { POST } from 'hooks/useSubmit';
import { FunctionComponent, useState } from 'react';
import { isEmail } from 'utils/isEmail';

interface EditEmailModalProps {
	showModal: boolean;
	setShowModal: (showModal: boolean) => void;
	account: MontelAccount;
	onSuccess: (newEmail: Email) => void;
}

export const EditEmailModal: FunctionComponent<EditEmailModalProps> = ({
	showModal,
	setShowModal,
	account,
	onSuccess,
}) => {
	const [newEmail, setNewEmail] = useState<string>('');
	const [emailError, setEmailError] = useState<string>('');

	const [changeEmail, changeEmailStatus] = useSubmit(
		`${ACCOUNT_API_ROUTE}/ChangeEmail`,
		POST,
		{
			messageOnSuccess: 'Email updated successfully',
			messageOnError: 'Error updating email',
			showApiError: true,
		},
	);

	const onChangeEmail = async () => {
		if (!isEmail(newEmail)) {
			setEmailError('A valid email is required');
			return;
		}

		const response = await changeEmail({
			accountId: account.id,
			email: newEmail,
		});
		if (response.success) {
			setShowModal(false);
			onSuccess({ value: newEmail, verified: false });
		}
	};

	const onCloseModal = () => {
		setNewEmail('');
		setEmailError('');
		setShowModal(false);
	};

	return (
		<StandardModal
			isOpen={showModal}
			onClose={onCloseModal}
			header={'Change email'}
			footer={
				<HStack gap="md">
					<Button
						text="Change email"
						size="sm"
						type="submit"
						isLoading={changeEmailStatus === RequestStatus.LOADING}
						loadingText="Updating..."
						onClick={onChangeEmail}
					/>
					<Button
						text="Cancel"
						size="sm"
						variant="secondary"
						onClick={onCloseModal}
					/>
				</HStack>
			}
		>
			<VStack gap="lg">
				<Text>
					{`This will change the email for ${account?.firstName} ${account?.lastName} - check hubspot for duplicate contacts!`}
				</Text>
				<Input
					label="Email"
					placeholder="Enter new email"
					autoComplete="off"
					name="email"
					value={newEmail}
					required
					onChange={(event) => setNewEmail(event.target.value)}
					errorMessage={emailError && emailError}
				/>
			</VStack>
		</StandardModal>
	);
};
